import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { Icon16Check } from '@figma/fpl-icons/icon-16-check';
import { Icon24ChevronDown } from '@figma/fpl-icons/icon-24-chevron-down';
import { Icon24ChevronUp } from '@figma/fpl-icons/icon-24-chevron-up';
import { useColorTheme } from '@figma/fpl-themes';
import clsx from 'clsx';
import { Description } from '../description/description.js';
import { useDropdownTheme } from '../dropdown-theme/dropdown-theme.js';
import { useIdProvider } from '../hooks/id-provider.js';
import { Container as Container$1, ScrollArrow, useSelect, Trigger as Trigger$1, Option as Option$1, Group as Group$1, Title as Title$1, Root as Root$1 } from '../select-primitive/select-primitive.js';
import { useString } from '../settings/settings.strings.js';
import { srOnly } from '../styles/composables.css.js';
import { container, scrollArrow, surface, theme, triggerContainer, triggerContent, trigger, width, size, inputGroup, option, check, optionText, group, groupLabel, lead, separator, subText } from './select.css.js';

/**
 * @compose { "ancestor": ["Select.Root"], "strictChildren": true }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Container = forwardRef(({ children, ...props }, ref) => {
    const mode = useDropdownTheme();
    const theme$1 = useColorTheme({
        mode: mode === 'dark' ? 'dark' : undefined,
    });
    return (jsx(Container$1, { "data-fpl-component": "", ...props, ...theme$1.dataAttributes, className: container, styles: {
            wrapper: clsx(surface, theme[theme$1.mode === 'light' ? 'light' : 'dark']),
        }, ref: ref, children: jsxs(theme$1.Context, { value: theme$1, children: [children, jsx(ScrollArrow, { className: scrollArrow, direction: "up", children: jsx(Icon24ChevronUp, {}) }), jsx(ScrollArrow, { className: scrollArrow, direction: "down", children: jsx(Icon24ChevronDown, {}) })] }) }));
});
Container.displayName = 'Select.Container';
/**
 * @compose { "ancestor": ["Select.Root"], "oneOf": "trigger" }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const ManuallyLabeledTrigger = forwardRef(({ placeholder, children, iconLead, width: width$1 = 'hug', size: size$1 = 'md', ...props }, ref) => {
    const { selectedItem, triggerLabel } = useSelect();
    const defaultPlaceholder = useString('select');
    const emptyText = placeholder ?? defaultPlaceholder;
    const noChildrenContent = selectedItem ? selectedItem.label : emptyText;
    return (jsxs(Trigger$1, { "data-fpl-component": "", className: clsx(trigger, width[width$1], size[size$1]), ref: ref, ...props, children: [iconLead && jsx(Lead, { children: iconLead }), jsxs("div", { className: triggerContainer, children: [jsx("span", { className: triggerContent, children: children ?? triggerLabel ?? noChildrenContent }), jsx(Icon24ChevronDown, {})] })] }));
});
ManuallyLabeledTrigger.displayName = 'Select.ManuallyLabeledTrigger';
/**
 * @compose { "ancestor": ["Select.Root"], "oneOf": "trigger" }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Trigger = forwardRef(({ label, description, ...props }, ref) => {
    const [id, Provider] = useIdProvider();
    return (jsx(Provider, { value: id, children: jsxs("div", { "data-fpl-component": "", className: inputGroup, children: [label, jsx(ManuallyLabeledTrigger, { id: id, ...props, ref: ref }), description && jsx(Description, { children: description })] }) }));
});
Trigger.displayName = 'Select.Trigger';
// A base component for Option and OptionReset
const OptionBase = forwardRef(({ children, iconLead, ...props }, ref) => {
    return (jsxs(Option$1, { "data-fpl-component": "", className: option, ...props, ref: ref, children: [jsx(Icon16Check, { "aria-hidden": true, className: check }), iconLead && jsx(Lead, { children: iconLead }), jsx("span", { className: optionText, children: children })] }));
});
OptionBase.displayName = 'Select.OptionBase';
const _Option = forwardRef((props, ref) => (jsx(OptionBase, { "data-fpl-component": "", ...props, ref: ref })));
_Option.displayName = 'Select.Option';
/**
 * @compose { "parent": ["Select.Container", "Select.Group"], "multi": true }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Option = _Option;
/**
 * @compose { "parent": ["Select.Container", "Select.Group"], "multi": true }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const OptionReset = forwardRef((props, ref) => (jsx(OptionBase, { "data-fpl-component": "", ...props, ref: ref })));
OptionReset.displayName = 'Select.OptionReset';
/**
 * @compose { "parent": ["Select.Container"], "multi": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Group = forwardRef((props, ref) => (jsx(Group$1, { "data-fpl-component": "", className: group, ref: ref, ...props })));
Group.displayName = 'Select.Group';
/**
 * @compose { "parent": ["Select.Group"], "oneOf": "title" }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Title = forwardRef((props, ref) => (jsx(Title$1, { "data-fpl-component": "", className: groupLabel, ref: ref, ...props })));
Title.displayName = 'Select.Title';
/**
 * @compose { "parent": ["Select.Group"], "oneOf": "title" }
 * @see https://fpl.figma.design/components/inputs/select/
 */
const HiddenTitle = forwardRef((props, ref) => (jsx(Title$1, { "data-fpl-component": "", className: srOnly, ref: ref, ...props })));
HiddenTitle.displayName = 'Select.HiddenTitle';
/**
 * A lead component used for within triggers or options
 * @see https://fpl.figma.design/components/inputs/select/
 */
const Lead = forwardRef((props, ref) => (jsx("span", { "data-fpl-component": "", className: lead, ref: ref, ...props })));
Lead.displayName = 'Select.Lead';
/** Differentiated text within a select option, rendered with secondary text colors. */
function SubText(props) {
    return jsx("div", { "data-fpl-component": "", className: subText, ...props });
}
SubText.displayName = 'Select.SubText';
/**
 * @compose { "root": true }
 * @see https://fpl.figma.design/components/inputs/select/
 */
function Root(props) {
    return (jsx(Root$1, { "data-fpl-component": "", ...props, padding: { top: 12, bottom: 12, left: 8, right: 8 }, offsetAmount: 8 }));
}
Root.displayName = 'Select.Root';
/**
 * Manually add a separator between options in a select dropdown. In most cases, you should use
 * `Select.Group` to add separators between options, as groups automatically have a visual separation
 * from surrounding options and provide a semantic label for the group. Only fall back to `Select.Separator`
 * if the separation you're adding is purely a visual nicety with no semantic meaning that should be
 * communicated to non-visual users.
 *
 * @compose { "parent": ["Select.Container"], "multi": true }
 */
function Separator(props) {
    // We hide the element from screen readers because otherwise it'll be included in the count
    // of selectable options
    return (jsx("li", { "data-fpl-component": "", ...props, "aria-hidden": true, className: separator, role: "separator" }));
}
Separator.displayName = 'Select.Separator';

export { Container, Group, HiddenTitle, Lead, ManuallyLabeledTrigger, Option, OptionReset, Root, Separator, SubText, Title, Trigger };
//# sourceMappingURL=select.js.map
